import React, { useState } from 'react';
import { MinusCircleIcon, PlusCircleIcon } from '@heroicons/react/24/solid';
import { WrenchScrewdriverIcon, CogIcon } from '@heroicons/react/24/outline';

const PartsAndServices = ({ jobCard, handleAddPart, handleAddService }) => {
  const [partsData, setPartsData] = useState(jobCard.partsRequired || [{ partName: '', type: 'generic', quantity: 1 }]);
  const [servicesData, setServicesData] = useState(jobCard.servicesRequired || [{ serviceType: '', technicianType: '', serviceQuantity: '' }]);

  const handlePartsInputChange = (event, index) => {
    const { name, value } = event.target;
    const updatedParts = [...partsData];
    updatedParts[index] = { ...updatedParts[index], [name]: value };
    setPartsData(updatedParts);
  };

  const handleServiceInputChange = (event, index) => {
    const { name, value } = event.target;
    const updatedServices = [...servicesData];
    updatedServices[index] = { ...updatedServices[index], [name]: value };
    setServicesData(updatedServices);
  };

  const handleAddPartClick = () => {
    const newPart = { partName: '', type: 'generic', quantity: 1 };
    const updatedParts = [...partsData, newPart];
    setPartsData(updatedParts);
    handleAddPart(newPart);
  };

  const handleRemovePartClick = (index) => {
    const updatedParts = [...partsData];
    updatedParts.splice(index, 1);
    setPartsData(updatedParts);
  };

  const handleAddServiceClick = () => {
    const newService = { serviceType: '', technicianType: '', serviceQuantity: '' };
    const updatedServices = [...servicesData, newService];
    setServicesData(updatedServices);
    handleAddService(newService);
  };

  const handleRemoveServiceClick = (index) => {
    const updatedServices = [...servicesData];
    updatedServices.splice(index, 1);
    setServicesData(updatedServices);
  };

  const incrementPartQuantity = (index) => {
    const updatedParts = [...partsData];
    updatedParts[index].quantity = Number(updatedParts[index].quantity) + 1;
    setPartsData(updatedParts);
  };

  const decrementPartQuantity = (index) => {
    const updatedParts = [...partsData];
    if (updatedParts[index].quantity > 1) {
      updatedParts[index].quantity = Number(updatedParts[index].quantity) - 1;
      setPartsData(updatedParts);
    }
  };

  return (
    <div className="space-y-6">
      {/* Parts Required Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center">
          <CogIcon className="h-6 w-6 text-white mr-2" />
          <h3 className="text-base font-black text-white uppercase">Parts Required</h3>
        </div>

        <div className="p-4">
          {/* Table Header */}
          <div className="grid grid-cols-4 gap-2 mb-2">
            <div className="text-xs font-bold text-gray-700 uppercase">Part Name</div>
            <div className="text-xs font-bold text-gray-700 uppercase">Type</div>
            <div className="text-xs font-bold text-gray-700 uppercase text-center">Quantity</div>
            <div className="text-xs font-bold text-gray-700 uppercase text-center">Actions</div>
          </div>

          {/* Parts Rows */}
          {partsData.map((item, index) => (
            <div key={index} className="grid grid-cols-4 gap-2 items-center mb-2">
              <input
                name="partName"
                value={item.partName}
                onChange={(e) => handlePartsInputChange(e, index)}
                className="px-2 py-1.5 text-xs border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                placeholder="Part Name"
              />
              <select
                name="type"
                value={item.type}
                onChange={(e) => handlePartsInputChange(e, index)}
                className="px-2 py-1.5 text-xs border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
              >
                <option value="generic">Generic</option>
                <option value="vehicle-specific">Vehicle-Specific</option>
              </select>
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => decrementPartQuantity(index)}
                  className="w-7 h-7 bg-gray-200 text-gray-700 rounded-l-lg hover:bg-gray-300 transition-colors font-bold text-sm"
                >
                  -
                </button>
                <input
                  type="text"
                  name="quantity"
                  className="w-12 px-2 py-1 text-xs text-center border-t-2 border-b-2 border-gray-300 focus:border-blue-500 focus:ring-0"
                  value={item.quantity}
                  onChange={(e) => handlePartsInputChange(e, index)}
                />
                <button
                  type="button"
                  onClick={() => incrementPartQuantity(index)}
                  className="w-7 h-7 bg-gray-200 text-gray-700 rounded-r-lg hover:bg-gray-300 transition-colors font-bold text-sm"
                >
                  +
                </button>
              </div>
              <div className="flex justify-center gap-1">
                {partsData.length !== 1 && (
                  <MinusCircleIcon
                    className="h-6 w-6 text-red-500 hover:text-red-700 cursor-pointer transition-colors"
                    onClick={() => handleRemovePartClick(index)}
                  />
                )}
                {partsData.length - 1 === index && (
                  <PlusCircleIcon
                    className="h-6 w-6 text-green-500 hover:text-green-700 cursor-pointer transition-colors"
                    onClick={handleAddPartClick}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Services Required Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 flex items-center">
          <WrenchScrewdriverIcon className="h-6 w-6 text-white mr-2" />
          <h3 className="text-base font-black text-white uppercase">Services Required</h3>
        </div>

        <div className="p-4">
          {/* Table Header */}
          <div className="grid grid-cols-4 gap-2 mb-2">
            <div className="text-xs font-bold text-gray-700 uppercase">Service Description</div>
            <div className="text-xs font-bold text-gray-700 uppercase">Technician Type</div>
            <div className="text-xs font-bold text-gray-700 uppercase text-center">Qty/Duration</div>
            <div className="text-xs font-bold text-gray-700 uppercase text-center">Actions</div>
          </div>

          {/* Service Rows */}
          {servicesData.map((item, index) => (
            <div key={index} className="grid grid-cols-4 gap-2 items-center mb-2">
              <input
                name="serviceType"
                placeholder="Service Description"
                value={item.serviceType}
                onChange={(event) => handleServiceInputChange(event, index)}
                className="px-2 py-1.5 text-xs border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
              />
              <input
                type="text"
                name="technicianType"
                placeholder="Technician Type"
                value={item.technicianType}
                onChange={(event) => handleServiceInputChange(event, index)}
                className="px-2 py-1.5 text-xs border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
              />
              <div className="flex justify-center">
                <input
                  type="number"
                  name="serviceQuantity"
                  placeholder="Quantity"
                  value={item.serviceQuantity}
                  onChange={(event) => handleServiceInputChange(event, index)}
                  className="w-20 px-2 py-1.5 text-xs text-center border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                />
              </div>
              <div className="flex justify-center gap-1">
                {servicesData.length !== 1 && (
                  <MinusCircleIcon
                    className="h-6 w-6 text-red-500 hover:text-red-700 cursor-pointer transition-colors"
                    onClick={() => handleRemoveServiceClick(index)}
                  />
                )}
                {servicesData.length - 1 === index && (
                  <PlusCircleIcon
                    className="h-6 w-6 text-green-500 hover:text-green-700 cursor-pointer transition-colors"
                    onClick={handleAddServiceClick}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PartsAndServices;
